import React, { useState } from 'react';
import { ExternalLink, Info, X, MapPin } from 'lucide-react';
import { createPortal } from 'react-dom';
import { mockData } from '../mock';
import { useT } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { SchemaEditor } from './admin/SchemaEditor';
import { pictureUrl } from '../lib/api';

const SCHEMA = [
  { key: 'name', label: 'Namn' },
  { key: 'tag', label: 'Kort merkelapp' },
  { key: 'href', label: 'Lenke', type: 'url' },
  { key: 'logo', label: 'Logo', type: 'image' },
  { key: 'logoBg', label: 'Kvit bakgrunn bak logoen', type: 'checkbox', hint: 'Bruk for mørke logoar' },
  { key: 'description', label: 'Tekst i info-popup', type: 'textarea', rows: 4 },
];

const Community = () => {
  const { t } = useT();
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const { content } = useContent();
  const items = content.community || mockData.community;

  return (
    <section id="naermiljo" className="bg-[#070409] border-t border-white/5 py-10 md:py-12" data-testid="community-section">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex items-center gap-3 mb-5">
          <MapPin size={14} className="text-[#ff2e2e]" />
          <p className="text-white/50 font-bold uppercase tracking-widest text-xs flex-1">{t('Dette skjer i nærmiljøet')}</p>
          <EditButton onClick={() => setEditing(true)} testId="edit-community-button" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`community-card-${c.id}`} className="group rounded-2xl bg-[#120c1b] border border-white/5 hover:border-[#ff2e2e]/40 transition-colors p-4 flex flex-col gap-3">
              <div className="h-12 flex items-center">
                <img src={pictureUrl(c.logo)} alt={c.name} loading="lazy" decoding="async" className={`max-h-12 w-auto object-contain ${c.logoBg ? 'rounded-lg bg-white p-1' : ''}`} />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm leading-tight truncate">{c.name}</p>
                <p className="text-white/40 text-xs truncate">{t(c.tag)}</p>
              </div>
              <div className="flex items-center gap-2 mt-auto">
                <button type="button" onClick={() => setSel(c)} data-testid={`community-info-${c.id}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/15 text-white/70 text-xs font-semibold hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors"><Info size={13} /> {t('Info')}</button>
                <a href={c.href} target="_blank" rel="noreferrer noopener" data-testid={`community-link-${c.id}`} aria-label={`${c.name} – opne nettside`} className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-white/60 hover:bg-[#ff2e2e] hover:text-black transition-colors"><ExternalLink size={13} /></a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && <SchemaEditor contentKey="community" title="Rediger «Dette skjer i nærmiljøet»" initial={items} schema={SCHEMA} itemLabel="aktør" onClose={() => setEditing(false)} />}
      {sel && createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="community-dialog">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-lg rounded-3xl bg-[#0f0a17] border border-white/10 p-7 md:p-8 shadow-2xl animate-[fadeUp_0.25s_ease-out]">
            <button type="button" onClick={() => setSel(null)} aria-label="Lukk" data-testid="community-dialog-close" className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-[#ff2e2e] hover:text-[#ff2e2e]"><X size={18} /></button>
            <div className="h-16 flex items-center mb-5"><img src={pictureUrl(sel.logo)} alt={sel.name} className={`max-h-16 w-auto object-contain ${sel.logoBg ? 'rounded-xl bg-white p-1.5' : ''}`} /></div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-xs mb-1">{t(sel.tag)}</p>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{sel.name}</h3>
            <p className="text-white/70 leading-relaxed mb-6">{t(sel.description)}</p>
            <a href={sel.href} target="_blank" rel="noreferrer noopener" data-testid="community-dialog-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#ff2e2e] text-black font-bold hover:bg-[#ff4d4d] transition-colors">
              {t('Gå til nettsida')} <ExternalLink size={16} />
            </a>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Community;
